/**
 * Centralised Mixpanel tracking composable.
 *
 * Usage:
 *   const { track, identify, reset } = useTracking();
 *   track('product_added_to_cart', { product_id: 1, product_name: 'Falafel Wrap' });
 */

// ── Menu curation (Group Select) shared property blocks ──
//
// Composed rather than repeated across the dozen curation events below, so a property can
// never mean one thing on `curation_dish_hidden` and another on `curation_confirmed`.
// Full definitions, and the dashboard notes that go with them, are in
// `docs/analytics-events.md`.

/** On EVERY curation event, so any of them can be broken down per maker. */
interface CurationContext {
  maker_id: string | number;
  maker_slug: string;
  maker_name: string;
  /** Null while curating a date that has no order yet (`create` mode). */
  order_id: string | number | null;
  is_recurate: boolean;
}

/**
 * The state of the menu at the moment of the event. `manually_hidden_dish_count` and
 * `rule_hidden_dish_count` deliberately **do not partition** `hidden_dish_count` — a dish can
 * be hidden by a requirement *and* hidden by hand. Use each as a yes/no signal for "did they
 * hand-curate" vs "did the filters do the work", not as shares of a total.
 */
interface CurationCounts {
  total_dish_count: number;
  available_dish_count: number;
  hidden_dish_count: number;
  manually_hidden_dish_count: number;
  rule_hidden_dish_count: number;
  revealed_dish_count: number;
  section_count: number;
  visible_section_count: number;
  hidden_section_count: number;
}

/** The requirements in force. Names as well as ids/keys, so a dashboard needs no lookup. */
interface CurationCriteria {
  dietary_keys: string[];
  dietary_count: number;
  allergen_ids: number[];
  allergen_names: string[];
  allergen_count: number;
  hidden_category_keys: string[];
  hidden_category_count: number;
  hidden_section_ids: number[];
  hidden_section_names: string[];
  requirement_count: number;
}

interface CurationDish {
  dish_id: number;
  dish_name: string;
  /** `Product.unified_category` — the AC's "product type". */
  product_type: string;
  section_id: number | null;
  section_name: string;
}

interface CurationSection {
  section_id: number;
  section_name: string;
  section_dish_count: number;
}

export interface TrackingEvents {
  // ── Auth ──
  login_completed: { method: "email" | "google" | "sso" };
  signup_completed: { method: "email" | "google" | "post_checkout" };
  logout: Record<string, never>;
  // SSO sign-in dead-ended before a WorkOS redirect could happen. domain is
  // the email's domain only — never the full address (see Mixpanel rules).
  sso_lookup_failed: { reason: "no_connection" | "invalid_email" | "lookup_error" | "sso_disabled"; domain?: string };
  // SSO sign-in dead-ended AFTER the browser left for the IdP — the other half of
  // the funnel. Without it a tenant whose connection is misconfigured is
  // indistinguishable from customers abandoning at their own IdP.
  //
  // First eight values are the backend's frozen SsoFailureReason codes arriving on
  // /auth/callback?sso=failed; the exchange_* / session_* values are this page's
  // own outcomes redeeming the one-time code. `unknown` is the fail-closed bucket
  // for a reason we don't recognise — the query param is attacker-controllable, so
  // it is matched against this list and never forwarded raw. NEVER the one-time
  // code and never the email.
  sso_login_failed: {
    reason:
      | "invalid_code"
      | "org_mismatch"
      | "identity_conflict"
      | "expired_state"
      | "invalid_state"
      | "internal_error"
      | "account_deactivated"
      | "sso_disabled"
      | "exchange_expired"
      | "exchange_failed"
      | "session_not_established"
      | "unknown";
  };

  // ── Discovery ──
  search_performed: { query: string };
  search_filtered: {
    source: "search" | "change_maker";
    filter_type: "date" | "tags" | "cuisines" | "combined";
    date?: string;
    tag_count?: number;
    occasion_count?: number;
    attribute_count?: number;
    cuisine_count?: number;
  };
  maker_viewed: { maker_slug: string; maker_name: string; source: "search" | "direct" | "premium" };
  // Homepage promotional banner clicked; banner_name comes from the Hub-set metadata.
  banner_clicked: { banner_name: string; banner_url: string };
  product_viewed: {
    product_id: string | number;
    product_name: string;
    maker_slug: string;
    price_gross?: number;
  };

  // ── Cart ──
  product_added_to_cart: {
    product_id: string | number;
    product_name: string;
    maker_slug: string;
    quantity: number;
    cart_total?: number;
    cart_item_count?: number;
  };
  product_removed_from_cart: {
    product_id: string | number;
    product_name: string;
    maker_slug: string;
  };
  cart_viewed: { item_count: number; cart_total: number; maker_slug?: string };

  // Re-ordering a past order. `rebuilt` is the outcome, not the click: recreateCart swallows its
  // own error and resolves undefined, so without it a basket that failed to rebuild is
  // indistinguishable from a successful one — which is exactly how ticket "Order again function
  // doesn't work" sat on live unseen.
  order_again: { maker_slug: string; item_count: number; rebuilt: boolean };

  // ── Checkout ──
  checkout_started: {
    item_count: number;
    cart_total: number;
    maker_slug?: string;
    is_logged_in: boolean;
  };
  checkout_step_completed: {
    step: number;
    step_name: string;
  };
  checkout_step_back: {
    from_step: number;
    from_step_name: string;
  };
  // The checkout dead-ended: a validate/create rejection the customer has to clear before
  // they can move on. Without it a stuck checkout is indistinguishable from abandonment in
  // the funnel — which is exactly how ticket 1052 (a half-typed delivery contact that
  // permanently blocked the step) ran on live for weeks unseen.
  checkout_blocked: {
    step_name: string;
    // Which request was rejected. Page-authored form gates are never tracked — see the
    // watcher in checkout.vue and ordersStore.checkoutErrorSource.
    source: "validate" | "create" | "payment" | "modify";
    // Server-authored copy (static OrderErrors strings) or our own gate copy. NEVER the
    // customer's typed value — no phone numbers, emails, addresses or search terms.
    reason: string;
  };
  order_created: {
    order_id: string | number;
    total: number;
    item_count: number;
    maker_slug?: string;
    has_cost_centre: boolean;
    has_discount: boolean;
    is_logged_in: boolean;
  };
  payment_initiated: {
    order_id: string | number;
    total: number;
    payment_type: "stripe" | "account" | "cost_centre";
  };
  // Customer placed an order paying on account (invoice) rather than by card.
  pay_on_account_used: {
    order_id: string | number;
    email?: string;
    organisation?: string;
  };

  // ── Order success ──
  order_completed: {
    order_id: string | number;
    total: number;
    item_count: number;
    maker_name?: string;
    is_new_customer: boolean;
  };
  post_checkout_account_created: Record<string, never>;

  // ── Premium ──
  premium_checkout_started: {
    order_id?: string;
    requires_payment: boolean;
		email?: string;
    organisation?: string;
  };
  premium_payment_completed: {
    order_id?: string;
    email?: string;
    organisation?: string;
  };

  // ── Order management (existing events, now typed) ──
  order_cancelled_by_customer: {
    order_id: string | number;
    email?: string;
    organisation?: string;
    cancellation_reason?: string;
  };
  order_delivery_updated_by_customer: { order_id: string | number };
  order_products_edit_started: {
    order_id: string | number;
    email?: string;
    organisation?: string;
    has_topup?: boolean;
    has_stripe?: boolean;
  };
  order_products_updated_by_customer: {
    order_id: string | number;
    item_count?: number;
    total?: number;
  };
  order_edit_payment_completed: {
    order_id: string | number;
  };

  // ── Menu curation (Group Select) ──
  //
  // The manager-facing "Customise menu" flow. Fired from `stores/menuCuration.ts`, except
  // `curation_confirmed`, which fires from the curate page because that is where all three
  // write modes (create / swap / recurate) converge.

  /** The Customise step opened AND its menu resolved — counts are meaningless before that. */
  curation_started: CurationContext &
    CurationCounts &
    CurationCriteria & {
      mode: "create" | "swap" | "recurate";
      /** What the order or the company profile arrived with, before any manager edit. */
      seeded_requirement_count: number;
    };

  curation_dish_hidden: CurationContext & CurationDish & CurationCounts;
  curation_dish_restored: CurationContext & CurationDish & CurationCounts;
  /** "Show anyway" — the manager overrode a requirement for one dish. */
  curation_dish_revealed: CurationContext & CurationDish & CurationCounts;
  /** "Hide it again" — that override withdrawn. */
  curation_dish_reveal_removed: CurationContext & CurationDish & CurationCounts;

  // No counts, unlike the dish events. Hiding a section is a server-evaluated rule, so the
  // counts are not true until the re-evaluation lands — they arrive on the
  // `curation_requirements_applied` that follows, tagged `trigger: "section_changed"`.
  curation_section_hidden: CurationContext & CurationSection;
  curation_section_restored: CurationContext & CurationSection;

  /** The requirements editor was expanded — the AC's "open Filter dishes modal". */
  curation_requirements_opened: CurationContext & CurationCriteria;
  /** One chip flipped. The interaction; `curation_requirements_applied` is the outcome. */
  curation_requirement_toggled: CurationContext & {
    requirement_type: "dietary" | "allergen" | "category";
    requirement_key: string;
    requirement_name: string;
    enabled: boolean;
  };
  /**
   * The server finished re-evaluating the menu against the requirements — the AC's "apply
   * filter". There is no Apply button: chips auto-apply through a 300 ms debounce, so a burst
   * of toggles coalesces into ONE of these, carrying the criteria that actually landed.
   */
  curation_requirements_applied: CurationContext &
    CurationCriteria &
    CurationCounts & {
      trigger: "requirement_toggled" | "section_changed" | "cleared" | "reset_to_baseline";
    };

  /** Switched to Employee preview. Switching back to editing is not tracked. */
  curation_preview_opened: CurationContext & CurationCounts;

  /**
   * `changes` = "Clear changes", back to what the order/company profile was seeded with.
   * `requirements` = "Clear requirements", all the way to empty.
   */
  curation_reset: CurationContext &
    CurationCriteria &
    CurationCounts & { reset_type: "changes" | "requirements" };

  curation_confirmed: CurationContext &
    CurationCounts &
    CurationCriteria & {
      mode: "create" | "swap" | "recurate";
      confirmed_order_id: string | number | null;
      delivery_date: string;
    };

  /**
   * Failure counterpart to `curation_confirmed`, same payload so the two divide cleanly into
   * a rate. Without it the funnel only ever shows successes and a confirm step that is
   * failing for everyone reads as a step nobody reaches.
   *
   * `reason` is a closed set of OUR slugs, never the backend's prose: those messages are
   * customer copy and are rewritten freely, which would fragment the metric a release at a
   * time. Adding a sixth failure branch on the page means adding it here first.
   */
  curation_failed: CurationContext &
    CurationCounts &
    CurationCriteria & {
      mode: "create" | "swap" | "recurate";
      reason:
        | "order_mismatch"
        | "maker_unresolved"
        | "swap_cancel_failed"
        | "auth_expired"
        // The re-curate PUT was REJECTED — `saveToOrder` returns false rather than throwing,
        // so this arm snackbars and returns and the outer catch never sees it. Without its own
        // slug a 409 re-curate showed as a `curation_started` with no confirm and no failure:
        // an invisible drop, and the most common one on this screen (Darko, PR #187).
        | "save_rejected"
        | "submit_failed";
    };

  /**
   * Employee side of the same story: the frozen menu drifted and their cart can no longer
   * check out. Fires on each NEWLY blocked set rather than every cart change, so it counts
   * occasions a customer was actually stopped, not keystrokes on a quantity field.
   *
   * Product ids and reason slugs only — no dish names, which would put a maker's menu into
   * the analytics payload for nothing a `product_id` join can't answer.
   */
  checkout_blocked_by_menu: {
    order_id: string | number;
    maker_slug: string;
    blocked_product_ids: number[];
    blocked_count: number;
    /** `OrderabilityFailureReasons` from the backend — `rule_violation`, `inactive`, … */
    reasons: string[];
  };

  // ── Discount ──
  discount_applied: { code: string; order_id?: string | number };
  discount_failed: { code: string; error?: string };

  // ── Account ──
  // Fired once per preference the customer changed when they save email prefs.
  email_preference_toggled: {
    preference: string;
    enabled: boolean;
    email?: string;
    organisation?: string;
  };

  cost_centre_membership_changed: {
    cost_centre_count: number;
    added_count: number;
    removed_count: number;
    is_pending: boolean;
  };
  member_added: { role: "employee" | "owner"; cost_centre_count: number };
  member_removed: { was_pending: boolean; is_self: boolean };
  member_invite_resent: { role: "employee" | "owner" };
  member_action_failed: {
    action: "add" | "remove" | "role_change" | "cost_centres" | "resend";
    status: number | null;
  };
  member_role_changed: {
    from_role: "employee" | "owner";
    to_role: "employee" | "owner";
    is_self: boolean;
    is_pending: boolean;
  };
}

type EventName = keyof TrackingEvents;

interface MixpanelInstance {
  track: (event: string, props?: Record<string, unknown>) => void;
  identify: (id: string) => void;
  alias: (alias: string, original?: string) => void;
  people: { set: (props: Record<string, unknown>) => void };
  reset: () => void;
}

export function useTracking() {
  const { $mixpanel } = useNuxtApp();
  const mp = $mixpanel as MixpanelInstance | undefined;

  function track<E extends EventName>(event: E, properties: TrackingEvents[E]) {
    mp?.track(event, properties as Record<string, unknown>);
  }

  function identify(userId: string | number, traits?: Record<string, unknown>) {
    mp?.identify(String(userId));
    if (mp && traits) {
      mp.people.set(traits);
    }
  }

  function reset() {
    mp?.reset();
  }

  return { track, identify, reset };
}
